import { SPEER_THANK_YOU_PATH } from './constants';
import { trackEvent } from './analytics';
import {
    LINKEDIN_MEETING_BOOKED_CONVERSION_ID,
    trackLinkedInConversion,
} from './linkedinAnalytics';
import { resolveMarketingAssetDownloadPath } from './marketingAssetDownloads';

declare global {
    interface Window {
        fbq?: (...args: unknown[]) => void;
        rdt?: (...args: unknown[]) => void;
    }
}

export type MarketingConversionType = 'webinar' | 'guide' | 'report' | 'meeting';

export type MarketingThankYouContent = {
    title: string;
    emailMessage: string;
    download?: {
        href: string;
        noun: 'guide' | 'report';
    };
};

const UTM_PARAM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
] as const;

/** LinkedIn conversion IDs — set in Netlify build env (Campaign Manager → Conversions). */
export const LINKEDIN_WEBINAR_REGISTRATION_CONVERSION_ID =
    process.env.REACT_APP_LINKEDIN_WEBINAR_REGISTRATION_CONVERSION_ID ?? '';

export const LINKEDIN_GUIDE_DOWNLOAD_CONVERSION_ID =
    process.env.REACT_APP_LINKEDIN_GUIDE_DOWNLOAD_CONVERSION_ID ?? '';

export function getUtmParams(): Record<string, string>
{
    if (typeof window === 'undefined')
    {
        return {};
    }

    const search = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};

    UTM_PARAM_KEYS.forEach((key) =>
    {
        const value = search.get(key);
        if (value)
        {
            utm[key] = value;
        }
    });

    return utm;
}

/** Merge current-page UTM query params into a lead payload for Sheet attribution. */
export function withUtmFields(payload: Record<string, string>): Record<string, string>
{
    return { ...payload, ...getUtmParams() };
}

export function resolveMarketingConversionType(
    payload: Record<string, string>,
): MarketingConversionType | undefined
{
    const type = payload.type?.trim();
    if (type === 'webinar' || type === 'guide' || type === 'report')
    {
        return type;
    }

    return undefined;
}

export function buildMarketingThankYouUrl(payload: Record<string, string>): string
{
    const conversion = resolveMarketingConversionType(payload);
    if (!conversion)
    {
        return SPEER_THANK_YOU_PATH;
    }

    const search = new URLSearchParams();
    search.set('conversion', conversion);

    if (payload.campaign)
    {
        search.set('campaign', payload.campaign);
    }

    if (payload.asset_name)
    {
        search.set('asset', payload.asset_name);
    }

    if (payload.report)
    {
        search.set('report', payload.report);
    }

    Object.entries(getUtmParams()).forEach(([ key, value ]) =>
    {
        search.set(key, value);
    });

    return `${SPEER_THANK_YOU_PATH}?${search.toString()}`;
}

/** Thank-you redirect after HubSpot meeting booked — preserves UTMs for attribution. */
export function buildMeetingBookedThankYouUrl(): string
{
    const search = new URLSearchParams();
    search.set('conversion', 'meeting');

    Object.entries(getUtmParams()).forEach(([ key, value ]) =>
    {
        search.set(key, value);
    });

    return `${SPEER_THANK_YOU_PATH}?${search.toString()}`;
}

function resolveLinkedInConversionId(conversion: MarketingConversionType): string
{
    if (conversion === 'webinar')
    {
        return LINKEDIN_WEBINAR_REGISTRATION_CONVERSION_ID;
    }

    if (conversion === 'meeting')
    {
        return LINKEDIN_MEETING_BOOKED_CONVERSION_ID;
    }

    if (conversion === 'guide' || conversion === 'report')
    {
        return LINKEDIN_GUIDE_DOWNLOAD_CONVERSION_ID;
    }

    return '';
}

export function trackMetaMarketingConversion(
    conversion: MarketingConversionType,
    params?: { campaign?: string; asset?: string },
): void
{
    if (typeof window === 'undefined' || typeof window.fbq !== 'function')
    {
        return;
    }

    const eventName = conversion === 'webinar'
        ? 'CompleteRegistration'
        : conversion === 'meeting'
            ? 'Schedule'
            : 'Lead';

    window.fbq('track', eventName, {
        content_name: params?.asset ?? params?.campaign ?? conversion,
        content_category: conversion,
    });
}

/** Reddit standard events: SignUp for webinars, Lead for guides/reports/meetings. */
export function trackRedditMarketingConversion(
    conversion: MarketingConversionType,
    params?: { campaign?: string; asset?: string },
): void
{
    if (typeof window === 'undefined' || typeof window.rdt !== 'function')
    {
        return;
    }

    const eventName = conversion === 'webinar' ? 'SignUp' : 'Lead';
    const conversionId = [
        conversion,
        params?.campaign ?? '',
        params?.asset ?? '',
        Date.now().toString(36),
    ].join(':');

    window.rdt('track', eventName, {
        conversionId,
        itemCount: 1,
    });
}

export function trackMarketingConversion(
    conversion: MarketingConversionType,
    params?: { campaign?: string; asset?: string },
): void
{
    const linkedInConversionId = resolveLinkedInConversionId(conversion);
    if (linkedInConversionId)
    {
        trackLinkedInConversion(linkedInConversionId);
    }

    trackMetaMarketingConversion(conversion, params);
    trackRedditMarketingConversion(conversion, params);

    trackEvent('marketing_conversion', {
        conversion_type: conversion,
        campaign: params?.campaign ?? '',
        asset: params?.asset ?? '',
    });
}

export function getMarketingThankYouContent(search: URLSearchParams): MarketingThankYouContent
{
    const conversion = search.get('conversion');

    if (conversion === 'guide' || conversion === 'report')
    {
        const downloadPath = resolveMarketingAssetDownloadPath({
            asset: search.get('asset'),
            report: search.get('report'),
        });

        return {
            title: 'Thank you for sharing your information.',
            emailMessage: `An email has been sent to your inbox with the ${conversion}.`,
            download: downloadPath
                ? { href: downloadPath, noun: conversion }
                : undefined,
        };
    }

    if (conversion === 'webinar')
    {
        return {
            title: 'Thank you for registering!',
            emailMessage: 'Check your inbox for webinar access details and next steps.',
        };
    }

    return {
        title: 'Your meeting is booked!',
        emailMessage: 'Thank you for booking with us. We\'re excited to potentially build with you.',
    };
}

/** Fire platform conversion pixels once per thank-you landing (session dedupe). */
export function trackMarketingThankYouFromUrl(): void
{
    if (typeof window === 'undefined')
    {
        return;
    }

    const search = new URLSearchParams(window.location.search);
    const conversion = search.get('conversion') as MarketingConversionType | null;

    if (!conversion || ! [ 'webinar', 'guide', 'report', 'meeting' ].includes(conversion))
    {
        return;
    }

    const campaign = search.get('campaign') ?? undefined;
    const asset = search.get('asset') ?? undefined;
    const dedupeKey = `speer_marketing_conversion:${conversion}:${campaign ?? ''}:${asset ?? ''}`;

    if (sessionStorage.getItem(dedupeKey))
    {
        return;
    }

    sessionStorage.setItem(dedupeKey, '1');
    trackMarketingConversion(conversion, { campaign, asset });
}
